import React from "react";

import { getParentElement } from "@impero-com/common/utils/dom";
import { ERRORED_CLASSNAME } from "@impero-com/common/utils/form/erroredElements";
import { FormField, FormLabel } from "@impero-com/ui";
import { Select } from "antd";
import type { DefaultOptionType } from "antd/es/select";
import { cx } from "class-variance-authority";
import { FilterFunc } from "rc-select/es/Select";
import { RefCallBack } from "react-hook-form";

import * as theme from "./theme.css";

export type SelectMultipleValueType = string | number;
export interface SelectMultipleOptionGroupType<T> {
    key: string | number;
    label: string;
    options: (DefaultOptionType & { value: T })[];
}
export type SelectMultipleOptionType<T> =
    | (DefaultOptionType & { value: T })[]
    | SelectMultipleOptionGroupType<T>[];

export interface SelectMultipleInputBaseProps<
    T extends SelectMultipleValueType,
> {
    options: SelectMultipleOptionType<T>;
    label?: string;
    disabled?: boolean;
    className?: string;
    showSearch?: boolean;
    // Note that antd default is `true`
    dropdownMatchSelectWidth?: boolean;
    filterOption?: boolean | FilterFunc<DefaultOptionType & { value: T }>;
    // The text rendered when the value is empty
    placeholder?: string;
    // Gives focus to this component when mounted
    autoFocus?: boolean;
    // Should be (in) the scrollable container
    getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
    nodeAfter?: React.ReactNode;
    // optional help text for form label
    helpText?: string;
    // optional search callback
    onSearch?: (value: string) => void;
    // Function to sort the selected values for display
    sortValues?: (values: T[]) => T[];
}

export interface SelectMultipleInputProps<
    T extends SelectMultipleValueType,
> extends SelectMultipleInputBaseProps<T> {
    value: T[];
    error?: string;
    fieldRef?: RefCallBack;
    onBlur?: () => void;
    onChange: (value: T[]) => void;
}

/**
 * Basic form component based on ant design's Select with multiple selection
 */
export function SelectMultipleInput<T extends SelectMultipleValueType>({
    options,
    label,
    filterOption,
    onChange,
    placeholder,
    className,
    nodeAfter,
    helpText,
    error,
    value,
    onBlur,
    fieldRef,
    sortValues,
    getPopupContainer = getParentElement,
    dropdownMatchSelectWidth = false,
    disabled = false,
    autoFocus = false,
    showSearch = false,
    onSearch,
}: SelectMultipleInputProps<T>) {
    let sortedValue = value;
    if (sortValues) {
        sortedValue = sortValues([...value]);
    }

    return (
        <FormField
            error={error}
            className={cx(error && ERRORED_CLASSNAME)}
            lite={label === undefined}
        >
            {label && (
                <FormLabel disabled={disabled} helpText={helpText}>
                    {label}
                </FormLabel>
            )}
            <div className={theme.rowDiv}>
                <Select<T[]>
                    ref={fieldRef}
                    options={options}
                    onBlur={onBlur}
                    className={cx(className, theme.selectInput)}
                    popupClassName={theme.selectInputPopup}
                    value={sortedValue}
                    placeholder={placeholder}
                    onChange={onChange}
                    disabled={disabled}
                    status={error !== undefined ? "error" : ""}
                    autoFocus={autoFocus}
                    showSearch={showSearch}
                    filterOption={filterOption as FilterFunc<DefaultOptionType>}
                    mode="multiple"
                    getPopupContainer={getPopupContainer}
                    dropdownMatchSelectWidth={dropdownMatchSelectWidth}
                    onSearch={onSearch}
                />
                {nodeAfter}
            </div>
        </FormField>
    );
}
